import { permissionKey } from "@/lib/rbacCatalog";
import { isPathVisibleByFlags } from "@/lib/navFeatureFlags";
import { SETTINGS_GATE_PERMISSIONS } from "@/lib/settingsAccess";

/**
 * Route → required read permission(s). First matching prefix wins.
 * @type {Array<{ match: (path: string) => boolean, keys: string[] }>}
 */
export const ROUTE_ACCESS_RULES = [
  { match: (p) => p === "/", keys: [permissionKey("dashboard", "read")] },
  {
    match: (p) =>
      p === "/tickets"
      || p.startsWith("/tickets/")
      || p.startsWith("/tickets-old/"),
    keys: [permissionKey("tickets", "read")],
  },
  {
    match: (p) => p === "/ffid" || p.startsWith("/ffid/"),
    keys: [permissionKey("ffid", "update")],
  },
  {
    match: (p) => p === "/trigger-workflow",
    keys: [permissionKey("trigger-workflow", "read")],
  },
  {
    match: (p) => p === "/agent-studio" || p.startsWith("/agent-studio/"),
    keys: [permissionKey("agent-studio", "read")],
  },
  {
    match: (p) => p.startsWith("/vibe-studio"),
    keys: [permissionKey("vibe-studio", "read")],
  },
  { match: (p) => p === "/discovery", keys: [permissionKey("discovery", "read")] },
  {
    match: (p) => p === "/deployment",
    keys: [permissionKey("deployment", "read")],
  },
  {
    match: (p) => p === "/abap-transports" || p.startsWith("/abap-transports/"),
    keys: [permissionKey("tickets", "read")],
  },
  { match: (p) => p === "/errors", keys: [permissionKey("errors", "read")] },
  { match: (p) => p === "/approvals", keys: [permissionKey("approvals", "read")] },
  { match: (p) => p === "/governance", keys: [permissionKey("approvals", "read")] },
  { match: (p) => p === "/analytics", keys: [permissionKey("analytics", "read")] },
  {
    match: (p) => p === "/business-value",
    keys: [permissionKey("business-value", "read")],
  },
  { match: (p) => p === "/reports", keys: [permissionKey("reports", "read")] },
  {
    match: (p) => p === "/documentation",
    keys: [permissionKey("documentation", "read")],
  },
  {
    match: (p) => p === "/settings",
    keys: [...SETTINGS_GATE_PERMISSIONS],
  },
  // SoD Rules BFF is under dashboardapi; gate with dashboard.read for now.
  { match: (p) => p === "/sod-rules", keys: [permissionKey("dashboard", "read")] },
  // Transformations (Halo) is in-app for now; any authenticated user can open it.
  {
    match: (p) =>
      p === "/transformations"
      || p.startsWith("/transformations/")
      || p === "/aura"
      || p.startsWith("/aura/"),
    keys: [],
  },
  { match: (p) => p === "/access-denied", keys: [] },
  { match: (p) => p === "/pending-access", keys: [] },
];

/**
 * @param {string} pathname
 * @returns {string[] | null} required permission keys; null = no rule (allow)
 */
export function requiredPermissionsForPath(pathname) {
  const path = pathname.split("?")[0];
  for (const rule of ROUTE_ACCESS_RULES) {
    if (rule.match(path)) {
      return rule.keys;
    }
  }
  return null;
}

/**
 * Preferred post-login paths when dashboard is not allowed.
 */
export const DEFAULT_HOME_CANDIDATES = [
  "/",
  "/tickets",
  "/ffid",
  "/trigger-workflow",
  "/agent-studio",
  "/vibe-studio",
  "/discovery",
  "/deployment",
  "/errors",
  "/approvals",
  "/analytics",
  "/business-value",
  "/reports",
  "/documentation",
  "/settings",
  "/transformations",
  "/aura",
];

export function canAccessRoute(pathname, hasPermission) {
  const keys = requiredPermissionsForPath(pathname);
  if (keys === null) return false;
  if (keys.length === 0) return true;
  return keys.some((key) => {
    const [resource, action] = key.split(":");
    return hasPermission(resource, action);
  });
}

/** Nav path → permission key for sidebar filtering. */
export const NAV_ITEM_PERMISSIONS = {
  "/": permissionKey("dashboard", "read"),
  "/tickets": permissionKey("tickets", "read"),
  "/ffid": permissionKey("ffid", "update"),
  "/trigger-workflow": permissionKey("trigger-workflow", "read"),
  "/agent-studio": permissionKey("agent-studio", "read"),
  "/vibe-studio": permissionKey("vibe-studio", "read"),
  "/vibe-studio/chat": permissionKey("vibe-studio", "read"),
  "/discovery": permissionKey("discovery", "read"),
  "/deployment": permissionKey("deployment", "read"),
  "/abap-transports": permissionKey("tickets", "read"),
  "/errors": permissionKey("errors", "read"),
  "/approvals": permissionKey("approvals", "read"),
  "/analytics": permissionKey("analytics", "read"),
  "/business-value": permissionKey("business-value", "read"),
  "/reports": permissionKey("reports", "read"),
  "/documentation": permissionKey("documentation", "read"),
  // SoD BFF under dashboardapi — same gate as Dashboard for now.
  "/sod-rules": permissionKey("dashboard", "read"),
};

export function canAccessNavPath(path, hasPermission) {
  if (path === "/settings") {
    return SETTINGS_GATE_PERMISSIONS.some((key) => {
      const [resource, action] = key.split(/:(.+)/);
      return hasPermission(resource, action);
    });
  }
  const key = NAV_ITEM_PERMISSIONS[path];
  if (!key) return true;
  const [resource, action] = key.split(/:(.+)/);
  return hasPermission(resource, action);
}

/**
 * First nav route the user may open (e.g. when "/" is denied).
 * @param {(resource: string, action: string) => boolean} hasPermission
 * @returns {string | null}
 */
/**
 * @param {(resource: string, action: string) => boolean} hasPermission
 * @param {Record<string, boolean>} [hiddenFlags]
 * @returns {string | null}
 */
export function findFirstAccessiblePath(hasPermission, hiddenFlags = {}) {
  for (const path of DEFAULT_HOME_CANDIDATES) {
    if (
      canAccessRoute(path, hasPermission)
      && isPathVisibleByFlags(path, hiddenFlags)
    ) {
      return path;
    }
  }
  return null;
}
